// Shared trust definitions; values and helpers are maintained here.
import { STATUS, CATEGORICAL, CHROME } from './palette';


// --- Provenance / trust tier (phase 2) -------------------------------------
// The backend's real vocabulary, found reading ingest/provenance.py and
// main.py directly (2026-09-20) rather than guessed: "first_party" (our own
// radio, protocol-verified), "first_party_unsigned" (our own MeshCore
// transport — ours, but MeshCore's own wire format has no signature, so the
// *content* can't be cryptographically verified even though the *source* is
// ours), "external_unauthenticated" (CoreScope, meshview, or any future
// third-party observer — phase 2 backend work, not live yet), and
// "simulated". Each gets its own label/treatment rather than a flattened
// verified/unverified boolean, per the platform's own non-negotiable: never
// present an unverified observation as verified, and a first-party-but-
// unsigned reading is a real, distinct middle case, not "verified."
//
// "relayed_mqtt" (phase 3/MQTT viewer, 2026-09-20): a community MQTT
// bridge — content arrives relayed through a third party's broker rather
// than heard directly over RF, and it's a distinct case from
// "external_unauthenticated" (a whole other OBSERVER polling our own data,
// e.g. CoreScope) — this is instead OUR OWN ingest subscribing to
// someone else's relay of the mesh's own traffic. Real backend value,
// found live on GET /api/mqtt/brokers and /api/packets/{hash}/coverage,
// 2026-09-20 — not guessed.
//
// "first_party_local_network" and "unverified" (found live, 2026-09-20,
// while building the connected-radio/firehose provenance filter — a real
// vocabulary GAP this closes: both were silently falling into "unknown"
// before this, on every surface that reads trust_tier, not just the new
// filter): read directly from the backend's own
// ingest/provenance.py, which classifies PER PACKET, not per observer —
// "first_party_local_network" is a LAN-local multicast-UDP reception
// (TRANSPORT_MULTICAST_UDP, firmware-reported ground truth, same
// evidentiary standard as RF first_party — just a different transport
// mechanism, not a weaker one). "unverified" means the packet's own
// transport_mechanism couldn't be determined AT ALL — the module's own
// docstring is explicit that this applies "regardless of whose it is,"
// including our own vnode's self-telemetry (confirmed live: 26 of a
// 300-packet sample, all observer_id=meshtastic_vnode, path=unknown) —
// so this is deliberately NOT treated as confirmed first-party here
// either, matching the backend's own "record honestly what we cannot
// distinguish rather than guess" rule.
export type TrustTier =
  | "first_party"
  | "first_party_local_network"
  | "first_party_unsigned"
  | "external_unauthenticated"
  | "relayed_mqtt"
  | "simulated"
  | "unverified"
  | "unknown";


export interface TrustTierInfo {
  key: TrustTier;
  label: string;
  short: string;
  verified: boolean; // true only for a genuinely first-party, protocol-verifiable reading
  // True only for a tier that means one of OUR OWN configured radios heard
  // this directly — the "connected radio" side of the firehose/radio-only
  // provenance filter below. Deliberately a SEPARATE flag from `verified`:
  // first_party_unsigned is ours (MeshCore, no signature) but not
  // cryptographically verifiable, so `verified` alone would wrongly
  // exclude our own MeshCore reception from "this radio only." A future
  // trust tier is scoped correctly the moment its entry below sets this
  // field — no component ever hardcodes a tier list.
  ownRadio: boolean;
  color: { light: string; dark: string };
}


export const TRUST_TIER_INFO: Record<TrustTier, TrustTierInfo> = {
  first_party: { key: "first_party", label: "First-party (our radio)", short: "Heard by our radio", verified: true, ownRadio: true, color: STATUS.good },
  first_party_local_network: {
    key: "first_party_local_network",
    label: "First-party, local network relay (our radio, LAN multicast rather than RF)",
    short: "First-party (LAN)",
    verified: true,
    ownRadio: true,
    color: STATUS.good,
  },
  first_party_unsigned: {
    key: "first_party_unsigned",
    label: "First-party, unsigned protocol (our radio, content not cryptographically verifiable)",
    short: "First-party (unsigned)",
    verified: false,
    ownRadio: true,
    color: STATUS.warning,
  },
  external_unauthenticated: {
    key: "external_unauthenticated",
    label: "External observer, unauthenticated (e.g. CoreScope, meshview)",
    short: "External (unverified)",
    verified: false,
    ownRadio: false,
    color: STATUS.serious,
  },
  relayed_mqtt: {
    key: "relayed_mqtt",
    label: "Relayed via a community MQTT bridge (not heard directly over RF)",
    short: "Via internet (MQTT)",
    verified: false,
    ownRadio: false,
    color: CATEGORICAL.blue,
  },
  simulated: { key: "simulated", label: "Simulated source", short: "Simulated", verified: false, ownRadio: false, color: CATEGORICAL.violet },
  // Distinct from "unknown" below: this means the reception path genuinely
  // could not be determined (see this section's own header comment) — a
  // real, reported statement, not an absence of one. Excluded from "this
  // radio only" on purpose, even though it's often our own vnode's own
  // traffic, because the backend itself declines to assume that.
  unverified: {
    key: "unverified",
    label: "Unverified reception path (couldn't determine how this was received)",
    short: "Unverified",
    verified: false,
    ownRadio: false,
    color: CHROME.inkMuted,
  },
  unknown: { key: "unknown", label: "No reception source reported", short: "Unknown", verified: false, ownRadio: false, color: CHROME.inkMuted },
};


export function normalizeTrustTier(raw: string | null | undefined): TrustTier {
  if (!raw) return "unknown";
  const key = raw.toLowerCase().trim();
  return key in TRUST_TIER_INFO ? (key as TrustTier) : "unknown";
}


export function trustTierInfo(raw: string | null | undefined): TrustTierInfo {
  return TRUST_TIER_INFO[normalizeTrustTier(raw)];
}


// --- Provenance scope filter (map/feed/histogram/status-strip) ------------
// Design goal: a checkbox to view the full firehose or just the connected
// radio. A fourth scoping axis, alongside protocol/region/
// node-selection. EXCLUDES rather than dims — the same treatment protocol
// gets, and for the same reason: keeping selected and unselected traffic
// visually separated, rather than a dimmed layer dense enough to obscure
// the ones actually selected, which applies here even more strongly: once
// meshview's firehose, its traceroutes, and 953 topology edges land,
// external observations will vastly outnumber first-party ones by the
// coordinator's own account — a dimmed firehose layer would still
// visually dominate the map by sheer volume, which is exactly the problem
// this control exists to solve. Dimming would not fix that; excluding
// does.
//
// Which trust tiers count as "this radio" vs "everything we can see" is
// TrustTierInfo's own `ownRadio` field above, never a separate hardcoded
// tier list here.
export type ProvenanceScope = "connected_radio" | "firehose";


// 2026-09-27 first-installer review (part5d, item 4): the same toggle used
// to be described three different ways across the Live Map (a "Filtered:"
// chip, this checkbox's own two-line label, and a bottom-of-map caption) —
// one label pair now, reused everywhere: "This radio only" / "Whole mesh".
export const PROVENANCE_SCOPE_LABEL: Record<ProvenanceScope, string> = {
  connected_radio: "This radio only",
  firehose: "Whole mesh",
};


// Default: connected-radio. This platform's whole premise (the
// coordinator's own framing) is "what we heard versus what someone told
// us" — defaulting to the verified, first-party picture is the honest
// answer to "what is my mesh doing," with the firehose as a deliberate,
// explicit opt-in to "what is everyone seeing." The alternative (firehose
// by default) means every new session opens already drowned in external
// volume, with the meaningful first-party signal invisible until someone
// notices and checks a box — the same failure this control exists to
// prevent, just deferred to first load instead of prevented outright.
export const DEFAULT_PROVENANCE_SCOPE: ProvenanceScope = "connected_radio";


export function isConnectedRadioTrustTier(raw: string | null | undefined): boolean {
  return trustTierInfo(raw).ownRadio;
}


export function packetInProvenanceScope(trustTier: string | null | undefined, scope: ProvenanceScope): boolean {
  return scope === "firehose" || isConnectedRadioTrustTier(trustTier);
}
